import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { getSession } from '../../../lib/session';
import { usePageTitle } from '../../../lib/hooks';
import {
  useUserDetail,
  useAssignUserRole,
  useGrantAgentAccess,
  useGrantStaffAccess,
} from '../../../features/users/network/users.api';
import { useRoles, useStaffList } from '../../../features/staff/network/staff.api';
import { useOrgUnits } from '../../../features/org/network/org.api';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { PageHeader } from '../../../components/layout/PageHeader';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Input } from '../../../components/ui/FormField';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/Dialog';
import { User, Shield, Briefcase, Smartphone, MapPin, UserCheck } from 'lucide-react';

export const Route = createFileRoute('/_auth/users/$id')({
  beforeLoad: ({ location, params }) => {
    if (params.id === 'new') {
      throw redirect({ to: '/users/new' });
    }
    const session = getSession();
    const isAllowed =
      session?.user.type === 'group_admin' ||
      session?.user.type === 'company_admin' ||
      session?.permissions.includes('admin.users.read') ||
      session?.permissions.includes('user.read');
    if (!isAllowed) {
      throw redirect({ to: '/', search: { callbackUrl: location.href } });
    }
  },
  component: UserDetailPage,
});

function UserDetailPage() {
  const { id } = Route.useParams() as { id: string };
  usePageTitle('User Details');

  // API queries
  const { data: user, isLoading, refetch } = useUserDetail(id);
  const { data: roles = [] } = useRoles();
  const { data: orgUnits = [] } = useOrgUnits();
  const { data: staffList = [] } = useStaffList();

  // Mutations
  const assignRoleMutation = useAssignUserRole();
  const grantAgentMutation = useGrantAgentAccess();
  const grantStaffMutation = useGrantStaffAccess();

  // Modal Dialog States
  const [roleDialogOpen, setRoleDialogOpen] = React.useState(false);
  const [selectedRoleId, setSelectedRoleId] = React.useState('');
  const [roleCompanyWide, setRoleCompanyWide] = React.useState(false);

  const [agentDialogOpen, setAgentDialogOpen] = React.useState(false);
  const [agentBranchId, setAgentBranchId] = React.useState('');
  const [agentSupervisorId, setAgentSupervisorId] = React.useState('');

  const [staffDialogOpen, setStaffDialogOpen] = React.useState(false);
  const [staffBranchId, setStaffBranchId] = React.useState('');
  const [staffRoleId, setStaffRoleId] = React.useState('');
  const [staffCompanyWide, setStaffCompanyWide] = React.useState(false);

  // Synchronize local states when dialogs open
  const handleOpenRoleDialog = () => {
    setSelectedRoleId(user?.roles?.[0]?.id || '');
    setRoleCompanyWide(user?.companyWide ?? false);
    setRoleDialogOpen(true);
  };

  const handleOpenAgentDialog = () => {
    setAgentBranchId(user?.orgUnits?.[0]?.id || '');
    setAgentSupervisorId('');
    setAgentDialogOpen(true);
  };

  const handleOpenStaffDialog = () => {
    setStaffBranchId(user?.orgUnits?.[0]?.id || '');
    setStaffRoleId(roles?.[0]?.id || '');
    setStaffCompanyWide(false);
    setStaffDialogOpen(true);
  };

  // Submission handlers
  const handleUpdateRole = async () => {
    if (!selectedRoleId) return;
    try {
      await assignRoleMutation.mutateAsync({ userId: id, roleId: selectedRoleId, companyWide: roleCompanyWide });
      setRoleDialogOpen(false);
      refetch();
    } catch (err) {
      console.error('Failed to assign role:', err);
    }
  };

  const handleGrantAgent = async () => {
    if (!agentBranchId) return;
    try {
      await grantAgentMutation.mutateAsync({
        userId: id,
        orgUnitId: agentBranchId,
        supervisorId: agentSupervisorId || null,
      });
      setAgentDialogOpen(false);
      refetch();
    } catch (err) {
      console.error('Failed to grant agent access:', err);
    }
  };

  const handleGrantStaff = async () => {
    if (!staffBranchId || !staffRoleId) return;
    try {
      await grantStaffMutation.mutateAsync({
        userId: id,
        orgUnitId: staffBranchId,
        roleId: staffRoleId,
        companyWide: staffCompanyWide,
      });
      setStaffDialogOpen(false);
      refetch();
    } catch (err) {
      console.error('Failed to grant staff access:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-sm text-text-secondary">Loading profile...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <span className="text-lg font-bold text-text-primary mb-2">User Not Found</span>
        <span className="text-sm text-text-secondary mb-4">The requested user profile does not exist.</span>
        <a href="/admin/staff" className="text-accent-500 hover:underline text-sm font-medium">
          Back to Directory
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full space-y-6" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title={user.name || 'Unnamed Identity'}
        subtitle={`Created on ${new Date(user.createdAt).toLocaleDateString()}`}
        backTo="/users"
        action={
          <StatusBadge
            status={user.status === 'active' ? 'success' : 'danger'}
            label={user.status === 'active' ? 'Active Login Access' : 'Suspended Access'}
          />
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card & Access info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-accent-500" />
              Identity Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Mobile Number</label>
                <div className="text-sm font-semibold text-text-primary">{user.phoneNumber || '-'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Email Address</label>
                <div className="text-sm font-semibold text-text-primary">{user.email || '-'}</div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Password Status</label>
                <div className="text-sm font-semibold text-text-primary">
                  {user.mustChangePassword ? 'Password reset required' : 'Active and configured'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">MPIN Status</label>
                <div className="text-sm font-semibold text-text-primary">
                  {user.mustResetMpin ? 'MPIN reset required' : 'Active and configured'}
                </div>
              </div>
            </div>
          </Card>

          {/* Type Profiles Details */}
          <div className="space-y-4">
            <h2 className="text-base font-bold text-text-primary mb-1">Application Surface Profiles</h2>

            {/* Staff Section */}
            {user.staffProfile ? (
              <Card className="p-5 border-l-[4px] border-l-blue-500 bg-surface-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-text-primary text-sm mb-1 flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-blue-500" />
                      Staff Back-Office Portal Profile
                    </h3>
                    <p className="text-xs text-text-secondary mb-3">
                      Authorizes back-office login, accounting, and system-wide admin capabilities.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-text-secondary font-medium">Assigned Name:</span>{' '}
                        <span className="text-text-primary font-semibold">{user.staffProfile.fullName}</span>
                      </div>
                      <div>
                        <span className="text-text-secondary font-medium">Staff Role Category:</span>{' '}
                        <span className="text-text-primary font-semibold capitalize">{user.staffProfile.type}</span>
                      </div>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    Active
                  </span>
                </div>
              </Card>
            ) : (
              <Card className="p-5 border-dashed bg-transparent border-border">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-text-primary text-sm flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-text-muted" />
                      Staff Portal Access
                    </h3>
                    <p className="text-xs text-text-secondary mt-1">
                      No staff profile linked. This user cannot access back-office web apps.
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleOpenStaffDialog}>
                    Grant Staff Access
                  </Button>
                </div>
              </Card>
            )}

            {/* Agent Section */}
            {user.agentProfile ? (
              <Card className="p-5 border-l-[4px] border-l-success-500 bg-surface-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-text-primary text-sm mb-1 flex items-center gap-2">
                      <Smartphone className="h-4 w-4 text-success-500" />
                      Agent Field Collections Mobile Profile
                    </h3>
                    <p className="text-xs text-text-secondary mb-3">
                      Authorizes field Collections Mobile App login for collections, receipts, and bid updates.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-text-secondary font-medium">Assigned Name:</span>{' '}
                        <span className="text-text-primary font-semibold">{user.agentProfile.fullName}</span>
                      </div>
                      <div>
                        <span className="text-text-secondary font-medium">Supervisor:</span>{' '}
                        <span className="text-text-primary font-semibold">
                          {user.agentProfile.supervisorName || 'None assigned'}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <a href={`/agents/${id}`} className="text-xs text-accent-500 hover:underline font-medium inline-flex items-center gap-1">
                        Open Agent Management Profile &rarr;
                      </a>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success-bg text-success-text border border-success-200">
                    Active
                  </span>
                </div>
              </Card>
            ) : (
              <Card className="p-5 border-dashed bg-transparent border-border">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-text-primary text-sm flex items-center gap-2">
                      <Smartphone className="h-4 w-4 text-text-muted" />
                      Agent Mobile Access
                    </h3>
                    <p className="text-xs text-text-secondary mt-1">
                      No agent profile linked. This user cannot perform field-agent mobile collections.
                    </p>
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleOpenAgentDialog}>
                    Grant Agent Access
                  </Button>
                </div>
              </Card>
            )}

            {/* Customer Section */}
            {user.customerProfile && (
              <Card className="p-5 border-l-[4px] border-l-warning-500 bg-surface-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-text-primary text-sm mb-1 flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-warning-500" />
                      Customer Portal Profile
                    </h3>
                    <p className="text-xs text-text-secondary mb-3">
                      Provides mobile customer dashboard, payment schedules, and UPI receipting.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-text-secondary font-medium">Assigned Name:</span>{' '}
                        <span className="text-text-primary font-semibold">{user.customerProfile.fullName}</span>
                      </div>
                      <div>
                        <span className="text-text-secondary font-medium">KYC Status:</span>{' '}
                        <span className="text-text-primary font-semibold uppercase">
                          {user.customerProfile.kycStatus}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <a href={`/customers/${id}`} className="text-xs text-accent-500 hover:underline font-medium inline-flex items-center gap-1">
                        Open Customer Profile &rarr;
                      </a>
                    </div>
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-warning-bg text-warning-text border border-warning-200">
                    Active
                  </span>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Roles & Permissions Card */}
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
                <Shield className="h-5 w-5 text-accent-500" />
                Assigned RBAC Roles
              </h2>
              {user.customerProfile && !user.staffProfile && !user.agentProfile ? null : (
                <Button variant="ghost" className="text-xs font-medium" onClick={handleOpenRoleDialog}>
                  Change Role
                </Button>
              )}
            </div>

            {user.customerProfile && !user.staffProfile && !user.agentProfile ? (
              <div>
                <p className="text-sm text-text-secondary mb-3">
                  This user is strictly a platform Customer. Customers have a fixed-permission set.
                </p>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-warning-bg text-warning-text border border-warning-200">
                  Customer Fixed Permissions
                </span>
              </div>
            ) : user.roles.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {user.roles.map((r: { id: string; name: string; companyWide?: boolean }) => (
                  <span
                    key={r.id}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-accent-50 text-accent-800 border border-accent-200 capitalize"
                  >
                    {r.name}
                    {r.companyWide && (
                      <span className="text-[10px] font-bold uppercase not-italic bg-accent-600 text-white rounded-full px-1.5 py-0.5">
                        All branches
                      </span>
                    )}
                  </span>
                ))}
              </div>
            ) : (
              <div>
                <p className="text-xs text-text-secondary mb-3">No system role assigned to this user yet.</p>
                <Button variant="secondary" size="sm" onClick={handleOpenRoleDialog}>
                  Assign Role
                </Button>
              </div>
            )}
          </Card>

          {/* Branches list */}
          <Card className="p-6">
            <h2 className="text-lg font-bold text-text-primary mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-accent-500" />
              Branch Memberships
            </h2>
            {user.orgUnits.length > 0 ? (
              <div className="space-y-2">
                {user.orgUnits.map((ou: { id: string; name: string; role?: string | null }) => (
                  <div key={ou.id} className="flex justify-between items-center p-2 rounded-lg bg-surface-2">
                    <span className="text-xs font-semibold text-text-primary">{ou.name}</span>
                    <span className="text-[10px] font-semibold text-text-secondary uppercase">{ou.role || 'Member'}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-text-secondary">No branch memberships assigned.</p>
            )}
          </Card>
        </div>
      </div>

      {/* Role Assignment Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Role</DialogTitle>
            <DialogDescription>
              Update the platform RBAC permissions role assigned to this identity.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label className="block text-xs font-medium text-text-secondary mb-2">Select System Role</label>
            <select
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
              className="w-full h-10 px-3 py-1 text-sm bg-surface-1 border border-border rounded-[6px] focus:outline-none focus:ring-1 focus:ring-accent-500"
            >
              <option value="">Select a role...</option>
              {roles.map((r: { id: string; name: string }) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>

            <label className="flex items-start gap-2 mt-4 cursor-pointer">
              <Input
                type="checkbox"
                checked={roleCompanyWide}
                onChange={(e) => setRoleCompanyWide(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-accent-500 focus:ring-accent-500 cursor-pointer"
              />
              <span className="text-xs">
                <span className="font-semibold text-text-primary block">Company-wide access</span>
                <span className="text-text-secondary">
                  Lifts branch scoping so this role applies across all branches. Without it, the user only sees their own branch's data.
                </span>
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setRoleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole} disabled={!selectedRoleId} loading={assignRoleMutation.isPending}>
              Assign Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Agent Access Dialog */}
      <Dialog open={agentDialogOpen} onOpenChange={setAgentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Agent Access</DialogTitle>
            <DialogDescription>
              Link an Agent Field Mobile profile, allowing this identity to log into mobile app collections.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-2">Primary Branch</label>
              <select
                value={agentBranchId}
                onChange={(e) => setAgentBranchId(e.target.value)}
                className="w-full h-10 px-3 py-1 text-sm bg-surface-1 border border-border rounded-[6px] focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                <option value="">Select branch...</option>
                {orgUnits.map((ou: { id: string; name: string }) => (
                  <option key={ou.id} value={ou.id}>
                    {ou.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-2">Supervisor (Staff)</label>
              <select
                value={agentSupervisorId}
                onChange={(e) => setAgentSupervisorId(e.target.value)}
                className="w-full h-10 px-3 py-1 text-sm bg-surface-1 border border-border rounded-[6px] focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                <option value="">Select supervisor (Optional)...</option>
                {staffList.map((s: { id: string; name: string }) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setAgentDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGrantAgent} disabled={!agentBranchId} loading={grantAgentMutation.isPending}>
              Grant Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Staff Access Dialog */}
      <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Staff Access</DialogTitle>
            <DialogDescription>
              Link a Staff Back-Office profile, allowing this identity to access the web portal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-xs font-medium text-text-secondary mb-2">Primary Branch</label>
              <select
                value={staffBranchId}
                onChange={(e) => setStaffBranchId(e.target.value)}
                className="w-full h-10 px-3 py-1 text-sm bg-surface-1 border border-border rounded-[6px] focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                <option value="">Select branch...</option>
                {orgUnits.map((ou: { id: string; name: string }) => (
                  <option key={ou.id} value={ou.id}>
                    {ou.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-text-secondary mb-2">RBAC System Role</label>
              <select
                value={staffRoleId}
                onChange={(e) => setStaffRoleId(e.target.value)}
                className="w-full h-10 px-3 py-1 text-sm bg-surface-1 border border-border rounded-[6px] focus:outline-none focus:ring-1 focus:ring-accent-500"
              >
                <option value="">Select role...</option>
                {roles.map((r: { id: string; name: string }) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-start gap-2 cursor-pointer">
              <Input
                type="checkbox"
                checked={staffCompanyWide}
                onChange={(e) => setStaffCompanyWide(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-accent-500 focus:ring-accent-500 cursor-pointer"
              />
              <span className="text-xs">
                <span className="font-semibold text-text-primary block">Company-wide access</span>
                <span className="text-text-secondary">
                  Lifts branch scoping so this role applies across all branches, not just the primary branch above.
                </span>
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setStaffDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGrantStaff}
              disabled={!staffBranchId || !staffRoleId}
              loading={grantStaffMutation.isPending}
            >
              Grant Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
